import React, { useEffect, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Button, Modal, Form, Row, Col, Card, Badge, Alert } from "react-bootstrap"
import {
    getAllAdvertisements,
    createAdvertisement,
    updateAdvertisement,
    deleteAdvertisement,
    activateAdvertisement,
    deactivateAdvertisement
} from "../../actions/commonController"
import PaginationTable from "../../components/paginationTable"
import { utcDate, formatDate } from "../../config/utcDate"
import { isEmpty } from "../../config/funcs"

const HeroManagement = () => {
    const [heroRecords, setHeroRecords] = useState([])
    const [loading, setLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [currentRecord, setCurrentRecord] = useState(null)
    const [formData, setFormData] = useState({
        sectionType: 'hero',
        heroTitle: '',
        heroSubtitle: '',
        startingPrice: '',
        paymentPlan: '',
        mediaSettings: {
            desktop: { type: 'image', file: null },
            tablet: { type: 'image', file: null },
            mobile: { type: 'image', file: null }
        },
        brochureFile: null,
        isActive: true
    })
    const [totalItems, setTotalItems] = useState(0)
    const [page, setPage] = useState(1)
    const [quantity, setQuantity] = useState(10)
    const [searchTerm, setSearchTerm] = useState('')
    const [initLoad, setInitLoad] = useState(false)

    const deviceOptions = ['desktop', 'tablet', 'mobile']

    useEffect(() => {
        initialization()
    }, [])

    const initialization = async () => {
        try {
            setLoading(true)
            await loadHeroRecords()
        } catch (err) {
            console.error("Error in initialization function", err)
        } finally {
            setLoading(false)
        }
    }

    const loadHeroRecords = useCallback(async () => {
        try {
            setLoading(true)

            // Get all hero/advertisement records
            const { status, data } = await getAllAdvertisements()

            if (status === 'success') {
                // Filter data based on search term only
                let filteredData = data || []

                // Apply search filter
                if (searchTerm) {
                    const searchLower = searchTerm.toLowerCase()
                    filteredData = filteredData.filter(item =>
                        (item.heroTitle && item.heroTitle.toLowerCase().includes(searchLower)) ||
                        (item.heroSubtitle && item.heroSubtitle.toLowerCase().includes(searchLower)) ||
                        (item.paymentPlan && item.paymentPlan.toLowerCase().includes(searchLower))
                    )
                }

                // Calculate pagination
                const total = filteredData.length
                setTotalItems(total)

                // Apply pagination
                const startIndex = (page - 1) * quantity
                const endIndex = startIndex + quantity
                const paginatedData = filteredData.slice(startIndex, endIndex)

                // Parse mediaSettings if it's a string
                const processedData = paginatedData.map(item => ({
                    ...item,
                    mediaSettings: typeof item.mediaSettings === 'string'
                        ? JSON.parse(item.mediaSettings)
                        : (item.mediaSettings || {})
                }))

                setHeroRecords(processedData)
            } else {
                toast.error('Failed to load hero records')
            }
        } catch (error) {
            console.error('Error loading hero records:', error)
            toast.error('Error loading hero records')
        } finally {
            setLoading(false)
        }
    }, [page, quantity, searchTerm])

    const handlePageChange = (newPage) => {
        setPage(newPage)
    }

    const handleItemsPerPageChange = (newRowsPerPage) => {
        setQuantity(newRowsPerPage)
        setPage(1)
    }

    const handleSearch = (e) => {
        setSearchTerm(e.target.value)
        setPage(1)
    }

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target

        if (name === 'heroTitle' || name === 'heroSubtitle' || name === 'sectionType' ||
            name === 'startingPrice' || name === 'paymentPlan') {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }))
        } else if (name === 'isActive') {
            setFormData(prev => ({
                ...prev,
                isActive: checked
            }))
        }
    }

    const handleMediaTypeChange = (device, type) => {
        setFormData(prev => ({
            ...prev,
            mediaSettings: {
                ...prev.mediaSettings,
                [device]: {
                    ...prev.mediaSettings[device],
                    type: type
                }
            }
        }))
    }

    const handleFileChange = (e) => {
        const { name, files } = e.target

        if (name === 'brochureFile') {
            setFormData(prev => ({
                ...prev,
                brochureFile: files[0]
            }))
        } else if (name.startsWith('desktop') || name.startsWith('tablet') || name.startsWith('mobile')) {
            // Extract device from filename
            let device = ''
            let fileType = ''

            if (name.startsWith('desktop')) {
                device = 'desktop'
                fileType = name.includes('Image') ? 'image' : 'video'
            } else if (name.startsWith('tablet')) {
                device = 'tablet'
                fileType = name.includes('Image') ? 'image' : 'video'
            } else if (name.startsWith('mobile')) {
                device = 'mobile'
                fileType = name.includes('Image') ? 'image' : 'video'
            }

            if (device) {
                setFormData(prev => ({
                    ...prev,
                    mediaSettings: {
                        ...prev.mediaSettings,
                        [device]: {
                            ...prev.mediaSettings[device],
                            file: files[0],
                            type: fileType
                        }
                    }
                }))
            }
        }
    }

    const getMediaSetting = (device, key) => {
        return formData.mediaSettings[device]?.[key] || (key === 'type' ? 'image' : null)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)

            // Create FormData object for file upload
            const formDataObj = new FormData()

            // Add text fields
            formDataObj.append('sectionType', formData.sectionType)
            formDataObj.append('heroTitle', formData.heroTitle)
            formDataObj.append('heroSubtitle', formData.heroSubtitle)
            formDataObj.append('startingPrice', formData.startingPrice)
            formDataObj.append('paymentPlan', formData.paymentPlan)
            formDataObj.append('isActive', formData.isActive)

            // Add media settings as JSON string
            const mediaSettingsToSave = {
                desktop: { type: formData.mediaSettings.desktop.type },
                tablet: { type: formData.mediaSettings.tablet.type },
                mobile: { type: formData.mediaSettings.mobile.type }
            }
            formDataObj.append('mediaSettings', JSON.stringify(mediaSettingsToSave))

            // Add files if they exist
            if (formData.brochureFile instanceof File) {
                formDataObj.append('brochureFile', formData.brochureFile)
            }

            // Add device-specific files
            deviceOptions.forEach(device => {
                const media = formData.mediaSettings[device]
                if (media?.file instanceof File) {
                    if (media.type === 'image') {
                        formDataObj.append(`${device}Image`, media.file)
                    } else if (media.type === 'video') {
                        formDataObj.append(`${device}Video`, media.file)
                    }
                }
            })

            let response
            if (editMode && currentRecord) {
                response = await updateAdvertisement(currentRecord.id, formDataObj)
            } else {
                response = await createAdvertisement(formDataObj)
            }

            if (response.status === 'success') {
                toast.success(editMode ? 'Hero record updated successfully' : 'Hero record created successfully')
                setShowModal(false)
                resetForm()
                loadHeroRecords()
            } else {
                toast.error(response.error || 'Operation failed')
            }
        } catch (error) {
            console.error('Error saving hero record:', error)
            toast.error('Error saving hero record')
        } finally {
            setLoading(false)
        }
    }

    const handleEdit = (record) => {
        setCurrentRecord(record)

        // Parse media settings
        const mediaSettings = typeof record.mediaSettings === 'string'
            ? JSON.parse(record.mediaSettings)
            : (record.mediaSettings || {})

        // Initialize device settings with defaults
        const defaultMediaSettings = {
            desktop: { type: 'image', file: null },
            tablet: { type: 'image', file: null },
            mobile: { type: 'image', file: null }
        }

        // Merge with existing settings
        const mergedSettings = {
            ...defaultMediaSettings,
            ...mediaSettings
        }

        setFormData({
            sectionType: record.sectionType || 'hero',
            heroTitle: record.heroTitle || '',
            heroSubtitle: record.heroSubtitle || '',
            startingPrice: record.startingPrice || '',
            paymentPlan: record.paymentPlan || '',
            mediaSettings: mergedSettings,
            brochureFile: null,
            isActive: record.isActive || true
        })
        setEditMode(true)
        setShowModal(true)
    }

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this hero record?')) {
            try {
                setLoading(true)
                const response = await deleteAdvertisement(id)
                if (response.status === 'success') {
                    toast.success('Hero record deleted successfully')
                    loadHeroRecords()
                } else {
                    toast.error(response.error || 'Failed to delete hero record')
                }
            } catch (error) {
                console.error('Error deleting hero record:', error)
                toast.error('Error deleting hero record')
            } finally {
                setLoading(false)
            }
        }
    }

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            setLoading(true)
            const response = await activateAdvertisement(id)

            if (response.status === 'success') {
                toast.success(`Hero record activated successfully`)
                loadHeroRecords()
            } else {
                toast.error(response.error || 'Failed to activate record')
            }
        } catch (error) {
            console.error('Error toggling status:', error)
            toast.error('Error updating status')
        } finally {
            setLoading(false)
        }
    }

    const handleActivate = async (id) => {
        try {
            setLoading(true)
            const { status, error, data } = await activateAdvertisement(id)
            if (status === 'success') {
                toast.success('Hero record activated successfully')
                loadHeroRecords()
            } else {
                toast.error(error || 'Failed to activate hero record')
            }
        } catch (error) {
            console.error('Error activating hero record:', error)
            toast.error('Error activating hero record')
        }
        finally {
            setLoading(false)
        }
    }

    const handleDeactivate = async (id) => {
        try {
            setLoading(true)
            const { status, error, data } = await deactivateAdvertisement(id)
            if (status === 'success') {
                toast.success('Hero record deactivated successfully')
                loadHeroRecords()
            }
            else {
                toast.error(error || 'Failed to deactivate hero record')
            }
        } catch (error) {
            console.error('Error deactivating hero record:', error)
            toast.error('Error deactivating hero record')
        }
        finally {
            setLoading(false)
        }
    }

    const resetForm = () => {
        setFormData({
            sectionType: 'hero',
            heroTitle: '',
            heroSubtitle: '',
            startingPrice: '',
            paymentPlan: '',
            mediaSettings: {
                desktop: { type: 'image', file: null },
                tablet: { type: 'image', file: null },
                mobile: { type: 'image', file: null }
            },
            brochureFile: null,
            isActive: true
        })
        setCurrentRecord(null)
        setEditMode(false)
    }

    const openAddModal = () => {
        resetForm()
        setShowModal(true)
    }

    useEffect(() => {
        if (initLoad) loadHeroRecords()
        setInitLoad(true)
    }, [page, quantity, searchTerm, loadHeroRecords])

    // Format payment plan for display
    const formatPaymentPlan = (plan) => {
        if (!plan) return '-'
        if (plan.length > 50) return plan.substring(0, 50) + '...'
        return plan
    }

    // Count active records
    const countActiveRecords = () => {
        return heroRecords.filter(record => record.isActive).length
    }

    // Get media summary for table display
    const getMediaSummary = (mediaSettings) => {
        if (!mediaSettings) return 'No media'

        const settings = typeof mediaSettings === 'string'
            ? JSON.parse(mediaSettings)
            : mediaSettings

        const devices = ['desktop', 'tablet', 'mobile']
        const summaries = devices.map(device => {
            const type = settings[device]?.type || 'image'
            return `${device.charAt(0).toUpperCase()}:${type.charAt(0).toUpperCase()}`
        })

        return summaries.join(' | ')
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-flex justify-content-between align-items-center">
                        <h4 className="page-title">Hero Section Management</h4>
                        <div>
                            <Button variant="primary" onClick={openAddModal}>
                                <i className="ri-add-line me-1"></i> Create
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Simple Search Bar */}
            {/* <div className="row mb-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <Form.Group>
                                <Form.Label>Search Hero Records</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by title, description, or payment plan..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </Form.Group>
                        </div>
                    </div>
                </div>
            </div> */}

            {/* Active Records Warning */}
            {countActiveRecords() >= 3 && (
                <div className="row mb-3">
                    <div className="col-12">
                        <Alert variant="warning">
                            <i className="ri-alarm-warning-line me-2"></i>
                            <strong>Maximum Active Records Reached:</strong> You have {countActiveRecords()} active hero records.
                            For optimal slideshow performance, we recommend keeping 3 active records.
                            You can deactivate some records if you want to activate new ones.
                        </Alert>
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-centered table-hover table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>ID</th>
                                                <th>Hero Title</th>
                                                <th>Starting Price / LTV</th>
                                                <th>Payment Plan / Starting Rate</th>
                                                <th>Media Settings</th>
                                                <th>Status</th>
                                                <th>Created</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {heroRecords.map((record) => (
                                                <tr key={record.id}>
                                                    <td>{record.id}</td>
                                                    <td>
                                                        <div className="fw-semibold">{record.heroTitle}</div>
                                                        <small className="text-muted text-truncate d-block" style={{ maxWidth: '200px' }}>
                                                            {record.heroSubtitle ?
                                                                (record.heroSubtitle.length > 60 ?
                                                                    record.heroSubtitle.substring(0, 60) + '...' :
                                                                    record.heroSubtitle)
                                                                : '-'}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold text-primary">
                                                            {record.startingPrice}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <small>{formatPaymentPlan(record.paymentPlan)}</small>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {getMediaSummary(record.mediaSettings)}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <Badge bg={record.isActive ? 'success' : 'secondary'}>
                                                            {record.isActive ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small>
                                                            {!isEmpty(record.createdAt) ? formatDate(utcDate(record.createdAt)) : "-"}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            {record.isActive ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-warning"
                                                                    onClick={() => handleDeactivate(record.id)}
                                                                    title="Deactivate"
                                                                    disabled={countActiveRecords() <= 1}
                                                                >
                                                                    <i className="ri-pause-line"></i>
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-success"
                                                                    onClick={() => handleActivate(record.id)}
                                                                    title="Activate"
                                                                    disabled={countActiveRecords() >= 3}
                                                                >
                                                                    <i className="ri-play-line"></i>
                                                                </Button>
                                                            )}
                                                            <Button
                                                                size="sm"
                                                                variant="outline-primary"
                                                                onClick={() => handleEdit(record)}
                                                            >
                                                                <i className="ri-edit-line"></i>
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() => handleDelete(record.id)}
                                                                disabled={record.isActive && countActiveRecords() <= 1}
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {heroRecords.length === 0 && (
                                        <div className="text-center py-4">
                                            <p>
                                                No hero records found. Add your first hero section to get started.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <PaginationTable
                totalItems={totalItems}
                itemsPerPage={quantity}
                pageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
            />

            {/* Hero Section Modal */}
            <Modal show={showModal} onHide={() => { setShowModal(false); resetForm() }} size="xl">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>{editMode ? 'Edit Hero Record' : 'Create New Advertisement'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Alert variant="info">
                            <strong>Note:</strong> You can have up to 3 active hero records for the slideshow.
                            Currently active: <strong>{countActiveRecords()}</strong>
                        </Alert>

                        <Form.Group className="mb-3">
                            <Form.Label>Hero Title *</Form.Label>
                            <Form.Control
                                type="text"
                                name="heroTitle"
                                value={formData.heroTitle}
                                onChange={handleInputChange}
                                placeholder="e.g., Discover Your Dream Home in Dubai"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Hero Description *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="heroSubtitle"
                                value={formData.heroSubtitle}
                                onChange={handleInputChange}
                                placeholder="e.g., Luxury properties with amazing amenities and prime locations"
                                required
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Starting Price (AED) or LTV </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="startingPrice"
                                        value={formData.startingPrice}
                                        onChange={handleInputChange}
                                        placeholder="Starting Price or LTV"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Payment Plan or Starting Rate</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="paymentPlan"
                                        value={formData.paymentPlan}
                                        onChange={handleInputChange}
                                        placeholder="Payment Plan or Starting Rate"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Enhanced Media Selection Section */}
                        <Card className="border mb-3">
                            <Card.Header className="bg-primary text-white">
                                <h6 className="mb-0">Flexible Media Settings by Device</h6>
                            </Card.Header>
                            <Card.Body>
                                <Alert variant="primary" className="small">
                                    <strong>Flexible Media:</strong> Choose between image or video for each device type independently
                                </Alert>

                                {/* Desktop Settings */}
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <h6 className="text-primary mb-3">
                                            <i className="ri-computer-line me-2"></i>
                                            Desktop Settings
                                        </h6>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Media Type</Form.Label>
                                                    <div>
                                                        <Form.Check
                                                            inline
                                                            type="radio"
                                                            label="Image"
                                                            name="desktopMediaType"
                                                            checked={getMediaSetting('desktop', 'type') === 'image'}
                                                            onChange={() => handleMediaTypeChange('desktop', 'image')}
                                                            className="custom-radio"
                                                        />
                                                        <Form.Check
                                                            inline
                                                            type="radio"
                                                            label="Video"
                                                            name="desktopMediaType"
                                                            checked={getMediaSetting('desktop', 'type') === 'video'}
                                                            onChange={() => handleMediaTypeChange('desktop', 'video')}
                                                            className="custom-radio"
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                            <Col md={8}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        {getMediaSetting('desktop', 'type') === 'image' ? 'Desktop Image' : 'Desktop Video'}
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        name={getMediaSetting('desktop', 'type') === 'image' ? 'desktopImage' : 'desktopVideo'}
                                                        accept={getMediaSetting('desktop', 'type') === 'image' ? 'image/*' : 'video/*'}
                                                        onChange={handleFileChange}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        {getMediaSetting('desktop', 'type') === 'image'
                                                            ? 'Recommended: 1920x1080px, JPG/PNG/WebP'
                                                            : 'Recommended: MP4, up to 10MB, 30s max'}
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                {/* Tablet Settings */}
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <h6 className="text-primary mb-3">
                                            <i className="ri-tablet-line me-2"></i>
                                            Tablet Settings
                                        </h6>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Media Type</Form.Label>
                                                    <div>
                                                        <Form.Check
                                                            inline
                                                            type="radio"
                                                            label="Image"
                                                            name="tabletMediaType"
                                                            checked={getMediaSetting('tablet', 'type') === 'image'}
                                                            onChange={() => handleMediaTypeChange('tablet', 'image')}
                                                            className="custom-radio"
                                                        />
                                                        <Form.Check
                                                            inline
                                                            type="radio"
                                                            label="Video"
                                                            name="tabletMediaType"
                                                            checked={getMediaSetting('tablet', 'type') === 'video'}
                                                            onChange={() => handleMediaTypeChange('tablet', 'video')}
                                                            className="custom-radio"
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                            <Col md={8}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        {getMediaSetting('tablet', 'type') === 'image' ? 'Tablet Image' : 'Tablet Video'}
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        name={getMediaSetting('tablet', 'type') === 'image' ? 'tabletImage' : 'tabletVideo'}
                                                        accept={getMediaSetting('tablet', 'type') === 'image' ? 'image/*' : 'video/*'}
                                                        onChange={handleFileChange}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        {getMediaSetting('tablet', 'type') === 'image'
                                                            ? 'Recommended: 1024x768px, JPG/PNG/WebP'
                                                            : 'Recommended: MP4, up to 8MB, 25s max'}
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                {/* Mobile Settings */}
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <h6 className="text-primary mb-3">
                                            <i className="ri-smartphone-line me-2"></i>
                                            Mobile Settings
                                        </h6>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Media Type</Form.Label>
                                                    <div>
                                                        <Form.Check
                                                            inline
                                                            type="radio"
                                                            label="Image"
                                                            name="mobileMediaType"
                                                            checked={getMediaSetting('mobile', 'type') === 'image'}
                                                            onChange={() => handleMediaTypeChange('mobile', 'image')}
                                                            className="custom-radio"
                                                        />
                                                        <Form.Check
                                                            inline
                                                            type="radio"
                                                            label="Video"
                                                            name="mobileMediaType"
                                                            checked={getMediaSetting('mobile', 'type') === 'video'}
                                                            onChange={() => handleMediaTypeChange('mobile', 'video')}
                                                            className="custom-radio"
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                            <Col md={8}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        {getMediaSetting('mobile', 'type') === 'image' ? 'Mobile Image' : 'Mobile Video'}
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        name={getMediaSetting('mobile', 'type') === 'image' ? 'mobileImage' : 'mobileVideo'}
                                                        accept={getMediaSetting('mobile', 'type') === 'image' ? 'image/*' : 'video/*'}
                                                        onChange={handleFileChange}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        {getMediaSetting('mobile', 'type') === 'image'
                                                            ? 'Recommended: 768x1024px, JPG/PNG/WebP'
                                                            : 'Recommended: MP4, up to 5MB, 20s max'}
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Form.Group className="mb-3">
                            <Form.Label>Brochure PDF</Form.Label>
                            <Form.Control
                                type="file"
                                name="brochureFile"
                                accept=".pdf"
                                onChange={handleFileChange}
                            />
                            <Form.Text className="text-muted">
                                Will be auto-downloaded when users submit forms
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <div className="mt-2">
                                <Form.Check
                                    type="switch"
                                    id="isActive"
                                    name="isActive"
                                    label={formData.isActive ? "Active" : "Inactive"}
                                    checked={formData.isActive}
                                    onChange={handleInputChange}
                                    disabled={!editMode && countActiveRecords() >= 3}
                                />
                                {!editMode && countActiveRecords() >= 3 && (
                                    <Form.Text className="text-danger">
                                        Cannot activate more than 3 records. Deactivate some existing records first.
                                    </Form.Text>
                                )}
                            </div>
                        </Form.Group>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowModal(false); resetForm() }}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Saving...' : editMode ? 'Update' : 'Create'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    )
}

export default HeroManagement